import { useMemo, useState } from "react";

import {
    Column,
    Icon,
    MEDIA,
    PriceLineChartWithAxis,
    Row,
    Text,
    ToggleSpacedButtons,
    useAppPalette,
    formatChartPercent
} from "@bridgesplit/ui";
import { RatesComparisonPeriodSelect, useRatesComparisonPeriodSelect } from "app/components/common";
import { bpsToUiDecimals, formatDate, formatPercent, getUnixTs, lamportsToUiAmount, TIME } from "@bridgesplit/utils";
import { BsMetaUtil, useLendingVaultHistoryQuery } from "@bridgesplit/abf-react";
import { useMediaQuery } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/dist/query";

import { useVaultContext } from "../../VaultContext";

enum Open {
    Apy = "APY",
    Deployed = "Deployed",
    Volume = "Volume"
}

type Data = {
    date: number;
    apy: number | undefined;
    totalApy: number | undefined;
    externalRewardsApy: number | undefined;
    deployed: number;
    volume: number;
};
const DATA: Data[] = [
    {
        date: getUnixTs(),
        apy: 0.15,
        totalApy: 0.18,
        externalRewardsApy: 0.0,
        deployed: 3399,
        volume: 500
    },
    {
        date: getUnixTs() + TIME.DAY,
        apy: 0.13,
        totalApy: 0.16,
        externalRewardsApy: 0.0,
        deployed: 10000,
        volume: 10000
    }
];

const OPEN_TO_FORMAT: Record<Open, (value: number) => string> = {
    [Open.Apy]: formatChartPercent,
    [Open.Deployed]: (value) => Intl.NumberFormat([], { notation: "compact" }).format(value),
    [Open.Volume]: (value) => Intl.NumberFormat([], { notation: "compact" }).format(value)
};

const OPEN_TO_KEY: Record<Open, keyof Data> = {
    [Open.Apy]: "apy",
    [Open.Deployed]: "deployed",
    [Open.Volume]: "volume"
};

type ChartKey = keyof Data;
type ChartKeyConfig = { key: ChartKey; color?: string };

const getChartKeys = (open: Open, hasAnyRewards: boolean, primary: string, secondary: string): ChartKeyConfig[] => {
    if (open === Open.Apy) {
        return hasAnyRewards
            ? [
                  { key: "apy", color: primary },
                  { key: "totalApy", color: secondary }
              ]
            : [
                  { key: "apy", color: primary },
                  { key: "totalApy", color: primary }
              ];
    }
    return [{ key: OPEN_TO_KEY[open] }];
};

// Add the ApyTooltipContent component
const ApyTooltipContent = ({ data }: { data: Data }) => {
    if (data.externalRewardsApy && data.externalRewardsApy > 0) {
        return (
            <Column spacing={0.5}>
                <Text>Base APY: {formatPercent(data.apy ?? 0)}</Text>
                <Text>Rewards APY: {formatPercent(data.externalRewardsApy ?? 0)}</Text>
                <Text>Total APY: {formatPercent(data.totalApy ?? 0)}</Text>
            </Column>
        );
    }

    return <Text>APY: {formatPercent(data.totalApy ?? 0)}</Text>;
};

export default function VaultCharts() {
    const { vaultAddress, vaultExpanded } = useVaultContext();
    const periodController = useRatesComparisonPeriodSelect("VaultChartsPeriod");
    const { secondary, primary } = useAppPalette();

    const { data: history } = useLendingVaultHistoryQuery(
        vaultAddress ? { vaultAddress, timeLookback: periodController.period } : skipToken,
        { skip: !vaultAddress }
    );

    const [open, setOpen] = useState<Open>(Open.Apy);

    const data = useMemo(() => {
        const decimals = vaultExpanded?.principalMetadata.decimals;
        if (!history?.length) {
            return DATA;
        }
        return history?.map((h): Data => {
            const baseApy = bpsToUiDecimals(h.wAvgApy);
            const externalRewardsApy = h.externalRewardsApy ? bpsToUiDecimals(h.externalRewardsApy) : 0;
            const totalApy = baseApy + externalRewardsApy;

            return {
                date: h.timestamp,
                apy: baseApy,
                totalApy: totalApy,
                externalRewardsApy: externalRewardsApy,
                deployed: lamportsToUiAmount(h.principalDeployed, decimals),
                volume: lamportsToUiAmount(h.cumulativeLoanVolume, decimals)
            };
        });
    }, [history, vaultExpanded]);

    // Transform data into separate series for points with and without rewards
    const transformedData = useMemo(() => {
        if (open !== Open.Apy) return data;

        const baseApySeries: (Data & { apy: number | undefined; totalApy: number | undefined })[] = [];
        const totalApySeries: (Data & { apy: number | undefined; totalApy: number | undefined })[] = [];

        data.forEach((point) => {
            const hasRewards = point.totalApy !== point.apy;

            if (hasRewards) {
                // Point has rewards, show both lines
                baseApySeries.push({ ...point });
                totalApySeries.push({ ...point });
            } else {
                // Point has no rewards, show only total APY
                baseApySeries.push({ ...point, totalApy: point.apy });
                totalApySeries.push({ ...point, totalApy: point.apy });
            }
        });

        return baseApySeries.concat(totalApySeries);
    }, [data, open]);

    // First, let's add a check if any point has rewards
    const hasAnyRewards = useMemo(() => {
        if (open !== Open.Apy) return false;
        return data.some((point) => point.totalApy !== point.apy);
    }, [data, open]);

    const isMobile = useMediaQuery(MEDIA.MD.below);

    return (
        <>
            <Row spaceBetween>
                <ToggleSpacedButtons
                    size="small"
                    value={open}
                    setValue={setOpen}
                    options={Object.values(Open).map((o) => ({ value: o }))}
                />
                <RatesComparisonPeriodSelect {...periodController} />
            </Row>

            <PriceLineChartWithAxis
                keys={getChartKeys(open, hasAnyRewards, primary, secondary)}
                height={200}
                minTickGap={50}
                formatYAxis={OPEN_TO_FORMAT[open]}
                formatXAxis={(seconds) => formatDate(seconds, "date", { showTime: "never" })}
                aspectRatio={isMobile ? 2 : 3}
                data={open === Open.Apy ? transformedData : data}
                tooltip={(point) => <Tooltip data={point} open={open} />}
            />
        </>
    );
}

function Tooltip({ data, open }: { data: Data | undefined; open: Open }) {
    const { vaultExpanded } = useVaultContext();

    const formattedValue = useMemo(() => {
        if (!data) return null;

        if (open === Open.Apy) {
            return <ApyTooltipContent data={data} />;
        }

        if (open === Open.Deployed) {
            return BsMetaUtil.formatAmount(vaultExpanded?.principalMetadata, data.deployed);
        }

        if (open === Open.Volume) {
            return BsMetaUtil.formatAmount(vaultExpanded?.principalMetadata, data.volume);
        }
    }, [data, open, vaultExpanded]);

    return (
        <Column spacing={0.5}>
            {formattedValue}
            <Text variant="body2" color="caption">
                <Icon type="date" /> {formatDate(data?.date)}
            </Text>
        </Column>
    );
}
